import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { EvaluatedViewport } from "building-types/views/planSets";
import { getReportableUnitDesignatorLabelIds } from "doc/utils/labelTools";
import { reportingUnitLabelFromEntityId } from "doc/utils/reportingUnitDocTools";

export interface ReportingUnitAttributedItem {
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
  readonly reportingUnitLabelId?: string;
}

/**
 * Extracts the plain reporting-unit label id used to filter the 3D linework image (both the live
 * preview and the realistic PDF/DXF export). Only applies to elevation/section drawings — plan views
 * are filtered during generation, and VIEW_3D keeps its own (greying) behavior untouched. The field
 * can still hold a ParameterRef (an object) when parameterized; only a resolved string id selects a
 * reporting unit.
 */
export function resolvedSelectedReportingUnitLabelId(viewport: EvaluatedViewport): string | undefined {
  if (viewport.viewType !== "ELEVATION_VIEW" && viewport.viewType !== "SECTION_VIEW") {
    return undefined;
  }
  return typeof viewport.selectedReportingUnitLabelId === "string" ? viewport.selectedReportingUnitLabelId : undefined;
}

/**
 * Resolves which reporting unit a geometry/prop belongs to: an explicit reportingUnitLabelId wins,
 * otherwise the entity handle is resolved up its hosting hierarchy. `memo` caches handle lookups
 * (keyed by serialized handle) across a single filtering pass. Shared by the elevation/section
 * previewer and the unit bounding-box calc so both agree on what belongs to the unit.
 */
export function resolveItemReportingUnitLabelId(
  doc: Doc,
  reportingUnitLabelIds: readonly string[],
  item: ReportingUnitAttributedItem,
  memo: Map<string, string | undefined>,
): string | undefined {
  if (item.reportingUnitLabelId !== undefined) {
    return item.reportingUnitLabelId;
  }
  const handle = item.entityHandleForSelectingReportingUnit;
  if (!handle) {
    return undefined;
  }
  const key = JSON.stringify(handle);
  if (memo.has(key)) {
    return memo.get(key);
  }
  const resolved = reportingUnitLabelFromEntityId(doc, reportingUnitLabelIds, handle);
  memo.set(key, resolved);
  return resolved;
}

/**
 * Builds a predicate keeping only the faces belonging to `selectedReportingUnitLabelId`. Each face
 * is resolved up its hosting hierarchy, or by the label the caller already attributed to it.
 */
export function makeReportingUnitBuildingFaceFilter(
  doc: Doc,
  selectedReportingUnitLabelId: string,
): (item: ReportingUnitAttributedItem) => boolean {
  const reportingUnitLabelIds = getReportableUnitDesignatorLabelIds(doc);
  const memo = new Map<string, string | undefined>();
  return (item) =>
    resolveItemReportingUnitLabelId(doc, reportingUnitLabelIds, item, memo) === selectedReportingUnitLabelId;
}
